// React
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

// External libraries
import { Alert, Box, Button, Card, Stack, Typography } from "@mui/material";

import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Project-specific imports
import { httpGet, httpPost } from "../../services/commonService";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";

import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import CustomAutoCompleteDropdown from "../../shared-components/form-controls/customAutoCompleteDropdown";

const placementPreferenceList = dataConstant.PlacementPreference;
const placementPreferenceDefaultValue = { value: "H", label: "Holding Tank" };

const PlacementPreference = () => {
  const authenticationState = useContext(AuthenticationContext);
  const { t } = useTranslation();
  const [snack, closeSnack, showSnackbar] = SnackState();

  const [state, setState] = useState({
    initialValue: null,
    isSubmitting: false,
    preference: {
      placementPreference: placementPreferenceDefaultValue,
    },
    placementPreferenceOptions: placementPreferenceList,
  });

  const methods = useForm({
    mode: "all",
    defaultValues: {
      placementPreference: placementPreferenceDefaultValue.value,
    },
  });

  const { setValue, handleSubmit, watch } = methods;
  const currentPreference = watch("placementPreference");
  const hasChanges =
    state.initialValue !== null && currentPreference !== state.initialValue;

  useEffect(() => {
    getPlacementPreferenceById();
  }, []);

  const getPlacementPreferenceById = () => {
    httpGet(
      urlConstant.PlacementPreference.GetPlacementPreferenceDetails,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        const responseData = response.Value.Data;

        let placementPreferenceValue = placementPreferenceDefaultValue;
        if (responseData.PlacementPreference !== null) {
          const found = placementPreferenceList.find(
            (i) => i.value === responseData.PlacementPreference
          );
          if (found) placementPreferenceValue = found;
        }

        setValue("placementPreference", placementPreferenceValue.value);

        setState((prevState) => ({
          ...prevState,
          initialValue: placementPreferenceValue.value,
          preference: {
            placementPreference: placementPreferenceValue,
          },
        }));
      })
      .catch((err) => {
        console.error(err);
        setState((prevState) => ({
          ...prevState,
        }));
      });
  };

  const onSubmit = (data) => {
    if (!hasChanges) {
      return;
    }

    setState((prev) => ({
      ...prev,
      isSubmitting: true,
    }));

    const preferenceValue = data.placementPreference?.value;

    const model = {
      PlacementPreference: preferenceValue,
    };

    httpPost(
      urlConstant.PlacementPreference.EditPlacementPreference,
      model,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          const successMessage = response.description;
          showSnackbar(successMessage, "success");

          setState((prev) => ({
            ...prev,
            initialValue: preferenceValue,
          }));

          getPlacementPreferenceById();
        } else {
          const errorMessage = response.description;
          showSnackbar(errorMessage, "error");
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
        }));
      });
  };

  const formConfig = {
    placementPreferenceId: {
      label: t("PlacementPreference.SelectLegs"),
      name: "placementPreference",
      dropDownList: state.placementPreferenceOptions,
    },
  };

  return (
    <>
      <Box flexDirection="column" gap={2} display="flex">
        <Typography
          color="secondary.light"
          display={{ xs: "block", md: "none" }}
          variant="h4"
          fontSize={"24px"}
          fontWeight={600}
        >
          {t("My Team")}
        </Typography>
        <Box width="100%">
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap="20px">
              <Alert
                sx={(theme) => ({
                  background: theme.palette.common.warningbg,
                  color: theme.palette.common.errortext,
                })}
                variant="filled"
                severity="info"
              >
                {t("PlacementPreference.Notes1")}
              </Alert>

              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {t("PlacementPreference.Placement Leg Preference")}
              </Typography>

              <Card
                sx={(theme) => ({
                  p: {
                    xs: "20px",
                    sm: "25px 30px",
                  },
                  background: theme.palette.common.black,
                  borderRadius: "20px",
                })}
              >
                <Typography color="secondary.light" variant="body1">
                  {t("PlacementPreference.Notes2")}
                </Typography>
                <Typography
                  color="secondary.light"
                  variant="body1"
                  sx={{ mt: 2 }}
                >
                  {t("PlacementPreference.Notes3")}
                </Typography>
              </Card>

              <Stack maxWidth="1200px" width="100%" margin="auto" gap="20px">
                <Stack gap="10px">
                  <Typography
                    color="secondary.light"
                    variant="h5"
                    fontWeight={600}
                  >
                    {t("PlacementPreference.SelectLeftLeg")}
                  </Typography>
                  <Typography
                    color="secondary.text"
                    variant="body1"
                    fontWeight={400}
                  >
                    {t("PlacementPreference.LeftLegDetails")}
                  </Typography>
                </Stack>

                <Stack gap="10px">
                  <Typography
                    color="secondary.light"
                    variant="h5"
                    fontWeight={600}
                  >
                    {t("PlacementPreference.SelectRightLeg")}
                  </Typography>
                  <Typography
                    color="secondary.text"
                    variant="body1"
                    fontWeight={400}
                  >
                    {t("PlacementPreference.RightLegDetails")}
                  </Typography>
                </Stack>

                <Stack gap="10px">
                  <Typography
                    color="secondary.light"
                    variant="h5"
                    fontWeight={600}
                  >
                    {t("PlacementPreference.SelectAlternateL-RLegs")}
                  </Typography>
                  <Typography
                    color="secondary.text"
                    variant="body1"
                    fontWeight={400}
                  >
                    {t("PlacementPreference.AlternateLegDetails")}
                  </Typography>
                </Stack>

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  style={{ width: "100%" }}
                >
                  <Card
                    sx={(theme) => ({
                      p: {
                        xs: "20px",
                        sm: "25px 30px",
                      },
                      background: theme.palette.common.headerbg,
                      borderRadius: "20px",
                      height: "100%",
                    })}
                  >
                    <Stack
                      direction="row"
                      alignItems="flex-end"
                      width="100%"
                      gap={2}
                    >
                      <Stack width="100%">
                        <CustomAutoCompleteDropdown
                          methods={methods}
                          properties={{
                            ...formConfig.placementPreferenceId,
                            isDisableClearable: true,
                          }}
                        />
                      </Stack>
                      <Button
                        type="submit"
                        sx={{ py: 1.5, px: 4 }}
                        variant="contained"
                        color="primary"
                        disabled={state.isSubmitting}
                      >
                        {t("Common.Save")}
                      </Button>
                    </Stack>
                  </Card>
                </form>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Box>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default PlacementPreference;
